import React, {Component} from 'react';
import Highcharts from "highcharts";
import { inject, observer } from 'mobx-react';
import moment from 'moment';

import Form, {FormBody, SelectInput, TextInput, DateInput, FormActions} from "../../Form";

import {station, observation, winhydro} from '../../../actions/api';

const {
    getAllAvailableStationIndexes,
    getAllRatingTableEntriesByStationIndex
} = winhydro;

@inject('hydrologyStore')
@observer
export default class VerificationTool extends Component {
    state = {
        stations: [],
        dataSeries: [],
        allRatings: [],
        tssStations: [],
        observationsDischarge: [],
        observationsWaterLevel: [],
        startDate: new Date(new Date().setDate(new Date().getDate() - 1)),
        endDate: new Date(),
        selectedStation: '',
        selectedDataSeriesWaterLevel: '',
        selectedDataSeriesDischarge: '',
    };

    chart = null;
    associatedTSSStation;

    componentDidMount() {
        getAllAvailableStationIndexes().then(res => {
            this.setState({stations: res});

            station.fetchAll().then(res => {
                if(res && !res.error)
                    this.setState({tssStations: res});
            });
        });
    }

    getObservations = (e) => {
        const {selectedDataSeriesDischarge, selectedDataSeriesWaterLevel, startDate, endDate} = this.state;

        if (selectedDataSeriesDischarge !== '' && selectedDataSeriesWaterLevel !== '') {
            observation.fetchAll({
                series_id: selectedDataSeriesWaterLevel,
                date_from: new Date(new Date(startDate).setHours(0, 0, 0, 0)).toISOString(),
                date_to: new Date(new Date(endDate).setHours(23, 59, 59, 999)).toISOString()
            }).then(resWaterLevel => {
                observation.fetchAll({
                    series_id: selectedDataSeriesDischarge,
                    date_from: new Date(startDate).toISOString(),
                    date_to: new Date(endDate).toISOString()
                }).then(resDischarge => {
                    this.setState({
                        observationsDischarge: resDischarge && resDischarge.data ? resDischarge.data : [],
                        observationsWaterLevel: resWaterLevel && resWaterLevel.data ? resWaterLevel.data : []
                    }, () => {
                        this.renderChart();
                    });
                });
            });
        }

        e.preventDefault();
    };

    handleStartDateChange = (e) => {
        this.setState({startDate: e.target.value});
    };

    handleEndDateChange = (e) => {
        this.setState({endDate: e.target.value});
    };

    handleDataSeriesWaterLevelChange = (e) => {
        this.setState({selectedDataSeriesWaterLevel: e.target.value});
    };

    handleDataSeriesDischargeChange = (e) => {
        this.setState({selectedDataSeriesDischarge: e.target.value});
    };

    handleStationIndexChange = (e) => {
        const {tssStations} = this.state;

        this.setState({selectedStation: e.target.value});

        if (e.target.value !== '') {
            this.associatedTSSStation = e.target.value !== '' && tssStations.find(station => !!(station.meta_data && station.meta_data.find(meta_data => meta_data.name === 'Station Index' && meta_data.value == e.target.value)));

            if (this.associatedTSSStation)
                station.fetchDataSeries(this.associatedTSSStation.id, {}).then(res => {
                    this.setState({dataSeries: res, selectedDataSeriesWaterLevel: '', selectedDataSeriesDischarge: ''});
                });

            getAllRatingTableEntriesByStationIndex(e.target.value).then(res => {
                this.setState({
                    allRatings: res.filter(item => !!item.rating_table).map(i => ({...i, color: this.random_rgb()})),
                    selectedDataSeriesWaterLevel: '',
                    selectedDataSeriesDischarge: '',
                    observationsWaterLevel: [],
                    observationsDischarge: []
                }, () => {
                    this.renderChart();
                });
            });
        } else {
            this.associatedTSSStation = null;

            this.setState({
                allRatings: [],
                selectedDataSeriesWaterLevel: '',
                selectedDataSeriesDischarge: '',
                dataSeries: [],
                observationsWaterLevel: [],
                observationsDischarge: []
            }, () => {
                this.renderChart();
            });
        }
    };

    random_rgb = () => {
        const o = Math.round, r = Math.random, s = 255;
        return 'rgb(' + o(r()*s) + ',' + o(r()*s) + ',' + o(r()*s) + ')';
    }

    renderChart = () => {
        const {allRatings, observationsDischarge, observationsWaterLevel} = this.state;

        const tssRatingCurve = [];

        observationsWaterLevel.forEach(wl => {
            if (wl.value && wl.datetime) {
                const correspondingObservationDischarge = observationsDischarge.find(d => new Date(d.datetime).getTime() === new Date(wl.datetime).getTime());

                if (correspondingObservationDischarge && correspondingObservationDischarge.value) {
                    tssRatingCurve.push({
                        stage: wl.value,
                        discharge: correspondingObservationDischarge.value,
                        datetime: wl.datetime
                    });
                }
            }
        });

        tssRatingCurve.sort((a, b) => {
            if (a.stage > b.stage) return 1; else if (a.stage < b.stage) return -1; else return 0;
        });

        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }

        const series = [...allRatings.map(ratingType => ({
            type: 'line',
            name: `Type No. ${ratingType.rating_type_no}, S.No. ${ratingType.serial_no} (${new Date(ratingType.start_date).toLocaleDateString()} - ${new Date(ratingType.end_date).toLocaleDateString()})`,
            data: ratingType.rating_table.map(rating => [rating.discharge, rating.stage]),
            color: ratingType.color
        }))];

        tssRatingCurve.forEach(rating => {
            const ratingTable = allRatings.find(rt => moment(rating.datetime).isBetween(moment(rt.start_date), moment(rt.end_date)));

            series.push({
                type: 'line',
                "showInLegend": false,
                name: `TSS Rating ${new Date(rating.datetime).toLocaleDateString()}`,
                data: [[rating.discharge, rating.stage]],
                color: ratingTable ? ratingTable.color : null
            });
        });

        if (allRatings.length > 0) {
            this.chart = Highcharts.chart('container', {
                chart: {
                    zoomType: 'x',
                    marginRight: 500
                },
                title: {
                    text: 'Rating Curve'
                },
                xAxis: {
                    title: {
                        useHTML: true,
                        text: 'Discharge in m<sup>3</sup>/s'
                    }
                },
                yAxis: {
                    title: {
                        text: 'Water Level in m'
                    }
                },
                legend: {
                    enabled: true,
                    align: 'right',
                    layout: 'vertical',
                    verticalAlign: 'top',
                },

                tooltip: {
                    crosshairs: true,
                    shared: true,
                    formatter: function () {
                        let response = `<div>`;

                        if(this.points) {
                            this.points.forEach(point => {
                                if(point && point.series && point.series.name)
                                    response += '<div><span style="color: #337ab7; font-weight: bold">' + point.series.name + ' </span><span style="color: #337ab7; font-weight: bold"> (H, Q): </span><span>(' + point.y + ', ' + this.x + ')</span><br/></div>';
                                else
                                    response += '<div><span style="color: #337ab7; font-weight: bold">Water Level in m: </span><span>' + point.y + '</span><br/></div>';

                            })
                        } else {
                            if(this && this.series && this.series.name)
                                response += '<div><span style="color: #337ab7; font-weight: bold">' + this.series.name + ' </span><br/><span style="color: #337ab7; font-weight: bold"> (H, Q): </span><span>(' + this.y + ', ' + this.x + ')</span></div>';
                            else
                                response += '<div><span style="color: #337ab7; font-weight: bold">Water Level in m: </span><span>' + this.y + '</span></div>';
                        }

                        response += '</div>';

                        return response;
                        },
                },

                series
            });
        }
    }

    render() {
        const {stations, selectedStation, allRatings, dataSeries, selectedDataSeriesDischarge, selectedDataSeriesWaterLevel, startDate, endDate} = this.state;

        return <div
            style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
            <Form>
                <FormBody>
                    <SelectInput label={'Station Index'}
                                 fields={{label: 'station_index', value: 'station_index'}}
                                 options={stations}
                                 onChange={this.handleStationIndexChange}
                                 value={selectedStation}/>
                    {selectedStation !== '' && this.associatedTSSStation && this.associatedTSSStation.identifier &&
                    <TextInput readOnly={true} label={'Identifier'} value={this.associatedTSSStation.identifier}/>}
                    {selectedStation !== '' && this.associatedTSSStation && this.associatedTSSStation.name &&
                    <TextInput readOnly={true} label={'Name'} value={this.associatedTSSStation.name}/>}
                </FormBody>
            </Form>
            {allRatings.length > 0 && <div style={{flex: 1, width: '100%'}}>
                <div id='container'/>
            </div>}
            {selectedStation !== '' && this.associatedTSSStation && <Form>
                <FormBody>
                    <SelectInput options={dataSeries}
                                 id={'data-series-water-level'}
                                 label={"Data Series (Water Level)"}
                                 fields={{label: 'name', value: 'id'}}
                                 onChange={this.handleDataSeriesWaterLevelChange}
                                 value={selectedDataSeriesWaterLevel}/>
                    <SelectInput options={dataSeries}
                                 id={'data-series-discharge'}
                                 label={"Data Series (Discharge)"}
                                 fields={{label: 'name', value: 'id'}}
                                 onChange={this.handleDataSeriesDischargeChange}
                                 value={selectedDataSeriesDischarge}/>
                    <DateInput value={moment(startDate).format('YYYY-MM-DD')}
                               onChange={this.handleStartDateChange}
                               label={'Start Date'}/>
                    <DateInput value={moment(endDate).format('YYYY-MM-DD')}
                               onChange={this.handleEndDateChange}
                               label={'End Date'}/>
                </FormBody>
                <FormActions>
                    <button onClick={this.getObservations}>View</button>
                </FormActions>
            </Form>}
        </div>
    }
}



// WEBPACK FOOTER //
// ./src/components/Hydrology/RT Method/VerificationTool.js